/**
 * usePresence — User presence tracking with REST polling + WebSocket updates
 *
 * Adapted from bachata-nation-next usePresence.ts.
 * Supports Hebrew time formatting.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { messagingApi } from '../api';
import { useAuthStore } from '@/features/auth';

export interface PresenceState {
  isOnline: boolean;
  lastSeen: string | null;
  lastSeenFormatted: string | null;
}

const PRESENCE_POLL_INTERVAL_MS = 10000; // 10 seconds

/**
 * Format "last seen" in human-readable form (Hebrew + English)
 */
export function formatLastSeen(lastSeen: string, locale: string): string {
  const date = new Date(lastSeen);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const isHebrew = locale === 'he';

  if (diff < 60 * 1000) {
    return isHebrew ? 'לפני רגע' : 'just now';
  }

  if (diff < 60 * 60 * 1000) {
    const mins = Math.floor(diff / (60 * 1000));
    if (isHebrew) return mins === 1 ? 'לפני דקה' : `לפני ${mins} דקות`;
    return mins === 1 ? '1 minute ago' : `${mins} minutes ago`;
  }

  if (diff < 24 * 60 * 60 * 1000) {
    const hours = Math.floor(diff / (60 * 60 * 1000));
    if (isHebrew) return hours === 1 ? 'לפני שעה' : `לפני ${hours} שעות`;
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }

  if (diff < 7 * 24 * 60 * 60 * 1000) {
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    if (isHebrew) return days === 1 ? 'אתמול' : `לפני ${days} ימים`;
    return days === 1 ? 'yesterday' : `${days} days ago`;
  }

  return date.toLocaleDateString(isHebrew ? 'he-IL' : 'en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Single user presence tracking
 */
export function usePresence(userId: string | null) {
  const [presence, setPresence] = useState<PresenceState>({
    isOnline: false,
    lastSeen: null,
    lastSeenFormatted: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const { i18n } = useTranslation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const fetchPresence = useCallback(async () => {
    if (!userId || !isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      const data = await messagingApi.getPresence([userId]);
      const userPresence = data.presence?.[userId];

      if (userPresence) {
        setPresence({
          isOnline: userPresence.isOnline,
          lastSeen: userPresence.lastSeen,
          lastSeenFormatted: userPresence.lastSeen
            ? formatLastSeen(userPresence.lastSeen, i18n.language)
            : null,
        });
      }
    } catch (error) {
      console.error('[usePresence] Failed to fetch:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, isAuthenticated, i18n.language]);

  useEffect(() => {
    fetchPresence();
  }, [fetchPresence]);

  // Poll every 10s
  useEffect(() => {
    if (!userId || !isAuthenticated) return;
    const interval = setInterval(fetchPresence, PRESENCE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [userId, isAuthenticated, fetchPresence]);

  // Update formatted time every minute
  useEffect(() => {
    if (!presence.lastSeen || presence.isOnline) return;
    const interval = setInterval(() => {
      setPresence(prev => ({
        ...prev,
        lastSeenFormatted: prev.lastSeen
          ? formatLastSeen(prev.lastSeen, i18n.language)
          : null,
      }));
    }, 60000);
    return () => clearInterval(interval);
  }, [presence.lastSeen, presence.isOnline, i18n.language]);

  /**
   * Update from WebSocket event
   */
  const updateFromWebSocket = useCallback(
    (update: { status: 'online' | 'away' | 'offline'; lastSeen?: number }) => {
      const isOnline = update.status === 'online';
      const lastSeenDate = update.lastSeen ? new Date(update.lastSeen).toISOString() : null;

      setPresence(prev => ({
        isOnline,
        lastSeen: isOnline ? prev.lastSeen : lastSeenDate || prev.lastSeen,
        lastSeenFormatted: isOnline || !lastSeenDate
          ? prev.lastSeenFormatted
          : formatLastSeen(lastSeenDate, i18n.language),
      }));
    },
    [i18n.language]
  );

  const refresh = useCallback(() => {
    setIsLoading(true);
    fetchPresence();
  }, [fetchPresence]);

  return {
    ...presence,
    isLoading,
    updateFromWebSocket,
    refresh,
  };
}

/**
 * Batch presence for multiple users
 */
export function useBatchPresence(userIds: string[]) {
  const [presenceMap, setPresenceMap] = useState<Record<string, { isOnline: boolean; lastSeen: string | null }>>({});
  const [isLoading, setIsLoading] = useState(userIds.length > 0);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userIdsKey = useMemo(() => userIds.join(','), [userIds]);

  const fetchBatch = useCallback(async () => {
    if (userIds.length === 0 || !isAuthenticated) {
      setPresenceMap({});
      setIsLoading(false);
      return;
    }

    try {
      const data = await messagingApi.getPresence(userIds);
      setPresenceMap(data.presence || {});
    } catch (error) {
      console.error('[useBatchPresence] Failed to fetch:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userIdsKey, userIds.length, isAuthenticated]);

  useEffect(() => {
    fetchBatch();
  }, [fetchBatch]);

  useEffect(() => {
    if (userIds.length === 0 || !isAuthenticated) return;
    const interval = setInterval(fetchBatch, PRESENCE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [userIds.length, isAuthenticated, fetchBatch]);

  return { presenceMap, isLoading };
}

export default usePresence;
